import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./ProcessActionPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faArrowLeft,
  faEdit,
  faTrash,
  faBolt,
  faArrowRightLong,
  faArrowRight,
  faCheckCircle,
  faExchangeAlt,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { ToastService } from "../../service/toast.service";
import { useLanguage } from "../../context/LanguageContext";
import { processApi, Process, ProcessAction } from "../../helper/processApi";
import { postUser } from "../../helper/http";

export default function ProcessActionPage() {
  const { id } = useParams<{ id: string }>();
  const processId = Number(id);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [process, setProcess] = useState<Process | null>(null);
  const [actions, setActions] = useState<ProcessAction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [processId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadProcess(), loadActions()]);
    setLoading(false);
  };

  const loadProcess = async () => {
    try {
      const res = await postUser("crud/list", {
        module: "process",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "id",
        filters: { id: processId },
      });

      if (res.data && res.data.length > 0) {
        setProcess(res.data[0]);
      }
    } catch (e) {
      console.error("Error loading process:", e);
    }
  };

  const loadActions = async () => {
    try {
      const actions = await processApi.getActions(processId);
      setActions(actions);
    } catch (e) {
      console.error("Error loading actions:", e);
      ToastService.error(t("loadError") || "Load error");
    }
  };

  const handleAddAction = () => {
    navigate(`/process-action-editor/${processId}/new`);
  };

  const handleEditAction = (actionId: number) => {
    navigate(`/process-action-editor/${processId}/${actionId}`);
  };

  const handleDeleteAction = async (actionId: number) => {
    if (!confirm(t("confirmDeleteAction") || "Delete this action?")) {
      return;
    }

    try {
      await processApi.deleteAction(actionId);
      ToastService.success(t("actionDeleted") || "Action deleted");
      await loadActions();
    } catch (e) {
      console.error("Error deleting action:", e);
      ToastService.error(t("deleteError") || "Delete error");
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t("loading") || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate("/processes")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className={styles.titleWrapper}>
            <h1>{process?.name || `Process #${processId}`}</h1>
            <span className={styles.subtitle}>
              {actions.length} {t("actions") || "actions configured"}
            </span>
          </div>
        </div>
        <button className={styles.addButton} onClick={handleAddAction}>
          <FontAwesomeIcon icon={faPlus} />
          {t("addAction") || "New Action"}
        </button>
      </div>

      <div className={styles.content}>
        {actions.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            <h3>{t("noActions") || "No actions yet"}</h3>
            <p>Define triggers and connect them to transactions.</p>
            <button className={styles.addButtonSecondary} onClick={handleAddAction}>
              {t("createFirstAction") || "Create Action"}
            </button>
          </div>
        ) : (
          <div className={styles.grid}>
            {actions.map((action) => (
              <div key={action.id} className={styles.card}>
                <div className={styles.sectionEvent}>
                  <div className={styles.sectionLabel}>Trigger</div>
                  <div className={styles.eventBadge}>
                    <FontAwesomeIcon icon={faBolt} className={styles.eventIcon} />
                    <span>{action.eventType}</span>
                  </div>
                </div>

                <div className={styles.connector}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>

                <div className={styles.sectionMiddle}>
                  <div className={styles.sectionLabel}>Validations</div>
                  {action.validations.length > 0 ? (
                    <div className={styles.validationList}>
                      {action.validations.map((validation, idx) => (
                        <div key={idx} className={styles.validationItem}>
                          <div className={styles.validationHeader}>
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className={styles.checkIcon}
                            />
                            <span className={styles.fieldName}>
                              {validation.fieldName}
                            </span>
                            {validation.isOptional && (
                              <span className={styles.optionalTag}>optional</span>
                            )}
                          </div>
                          <div className={styles.rulesContainer}>
                            {validation.validators.map((rule, rIdx) => {
                                const [name, param] = rule.split(":", 2);
                                return (
                                    <span key={rIdx} className={styles.ruleTag}>
                                        {name} {param && <b>({param})</b>}
                                    </span>
                                )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noValidations}>
                      No specific validations
                    </div>
                  )}
                </div>

                <div className={styles.connector}>
                  <FontAwesomeIcon icon={faArrowRightLong} />
                </div>

                <div className={styles.sectionResult}>
                  <div className={styles.sectionLabel}>Execute</div>
                  <div
                    className={`${styles.transactionBox} ${
                      !action.transactionId ? styles.noTransaction : ""
                    }`}
                    onClick={() =>
                      action.transactionId &&
                      navigate(`/transaction-template/${action.transactionId}`)
                    }
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>
                      {action.transactionId
                        ? `Transaction #${action.transactionId}`
                        : "No Transaction Linked"}
                    </span>
                  </div>
                </div>

                <div className={styles.cardActions}>
                  <button
                    className={styles.iconBtn}
                    onClick={() => handleEditAction(action.id)}
                    title={t("edit")}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className={`${styles.iconBtn} ${styles.deleteBtn}`}
                    onClick={() => handleDeleteAction(action.id)}
                    title={t("delete")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}